import type { ICotizacionServicios } from '../../../modelos/CotizadorServicios/entidades/ICotizacionServicios';
import type { IConfiguracionServicios } from '../../../modelos/CotizadorServicios/entidades/IConfiguracionServicios';
import type { IEquipoServicioAscensor } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioAscensor';
import type { IEquipoServicioEscalera } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioEscalera';
import type { IOpcionesCotizacionServicios, IOpcionSelectServicio } from '../../../modelos/CotizadorServicios/entidades/IOpcionesCotizacionServicios';
import type { IFilaTotalesPlanes, IResultadoCotizacionServicios, ITotalesFamilia } from '../../../modelos/CotizadorServicios/entidades/IResultadoCotizacionServicios';
import { generarIdTemporal } from '../../configuracionCotizadorServicios/interfaces';

export type TipoEquipoServicio = 'ascensor' | 'escalera';

export const cotizacionServiciosInicial: ICotizacionServicios = {
    Proyecto: '',
    Cliente: '',
    Anio: new Date().getFullYear(),
    DescuentoAscensores: 0,
    IncrementoAscensores: 0,
    DescuentoEscaleras: 0,
    IncrementoEscaleras: 0,
    Ascensores: [],
    Escaleras: [],
    // Datos del formato del Excel; los confirma el back al guardar.
    CodigoFormato: 'CMS-FO-02',
    VersionFormato: '1.0'
};

export const nuevoAscensor = (): IEquipoServicioAscensor => ({
    Id: generarIdTemporal(),
    TipoAscensor: '',
    Referencia: '',
    Capacidad: 0,
    Velocidad: 0,
    Paradas: 0,
    Cantidad: 1,
    TipoUsoId: '',
    HorarioId: ''
});

export const nuevaEscalera = (): IEquipoServicioEscalera => ({
    Id: generarIdTemporal(),
    TipoEquipoId: '',
    Referencia: '',
    Recorrido: 0,
    Velocidad: 0,
    Cantidad: 1,
    EntornoOperativoId: '',
    HorarioId: ''
});

/** Campos que deben quedar diligenciados para guardar la fila. */
export const ascensorCompleto = (a: IEquipoServicioAscensor) =>
    !!(a.TipoAscensor && a.Referencia.trim() && a.Capacidad && a.Velocidad && a.Paradas && a.Cantidad && a.TipoUsoId && a.HorarioId);

export const escaleraCompleta = (e: IEquipoServicioEscalera) =>
    !!(e.TipoEquipoId && e.Referencia.trim() && e.Recorrido && e.Velocidad && e.Cantidad && e.EntornoOperativoId && e.HorarioId);

const opcionesDeFactores = (filas: { Id: string; Nombre: string }[]): IOpcionSelectServicio[] =>
    filas.filter(f => f.Nombre.trim()).map(f => ({ value: f.Id, label: f.Nombre }));

const opcionesDeVelocidades = (filas: { Velocidad: number }[]): IOpcionSelectServicio[] =>
    filas.map(f => ({ value: String(f.Velocidad), label: `${f.Velocidad} m/s` }));

/** Opciones de los selectores de la cotización a partir de la configuración. */
export const construirOpcionesCotizacion = (configuracion: IConfiguracionServicios): IOpcionesCotizacionServicios => ({
    ascensores: {
        tipos: configuracion.TiposAscensor.map(t => ({ value: t, label: t })),
        capacidades: configuracion.Capacidades.map(c => ({ value: c, label: `${Number(c).toLocaleString('es-CO')} kg` })),
        velocidades: opcionesDeVelocidades(configuracion.VelocidadesAscensor),
        usos: opcionesDeFactores(configuracion.TiposUso)
    },
    escaleras: {
        tipos: opcionesDeFactores(configuracion.TiposEquipoEscalera),
        velocidades: opcionesDeVelocidades(configuracion.VelocidadesEscalera),
        entornos: opcionesDeFactores(configuracion.EntornosOperativos)
    },
    horarios: opcionesDeFactores(configuracion.HorariosTrabajo)
});

/** Nombre visible de una opción (para mostrar la fila fuera de edición). */
export const etiquetaOpcion = (opciones: IOpcionSelectServicio[], valor: string | number) =>
    opciones.find(o => o.value === String(valor))?.label ?? '-';

const precioCero = () => ({ Unitario: 0, Total: 0 });
const filaTotalesCero = (): IFilaTotalesPlanes => ({ PlanConekta: precioCero(), PlanConektaPlus: precioCero() });
const totalesCero = (): ITotalesFamilia => ({ SinIva: filaTotalesCero(), Iva: filaTotalesCero(), ConIva: filaTotalesCero() });

/**
 * Resultado con los equipos de la cotización y los valores en cero.
 * Solo para ver el diseño del Resumen: los valores los calcula el back.
 */
export const resultadoSinCalcular = (cotizacion: ICotizacionServicios): IResultadoCotizacionServicios => ({
    Ascensores: {
        Lineas: cotizacion.Ascensores.map(a => ({
            EquipoId: a.Id, Cantidad: a.Cantidad, Referencia: a.Referencia, Capacidad: a.Capacidad,
            Velocidad: a.Velocidad, Paradas: a.Paradas, PlanConekta: precioCero(), PlanConektaPlus: precioCero()
        })),
        Totales: totalesCero()
    },
    Escaleras: {
        Lineas: cotizacion.Escaleras.map(e => ({
            EquipoId: e.Id, Cantidad: e.Cantidad, Referencia: e.Referencia, Recorrido: e.Recorrido,
            Velocidad: e.Velocidad, PlanConekta: precioCero(), PlanConektaPlus: precioCero()
        })),
        Totales: totalesCero()
    },
    Desglose: [
        ...cotizacion.Ascensores.map(a => ({ EquipoId: a.Id, Referencia: a.Referencia, EsAscensor: true, Cantidad: a.Cantidad })),
        ...cotizacion.Escaleras.map(e => ({ EquipoId: e.Id, Referencia: e.Referencia, EsAscensor: false, Cantidad: e.Cantidad }))
    ].map(equipo => ({
        ...equipo,
        CostoBase: 0, CostoHorario: 0, TotalCostoBase: 0, CostoCif: 0, CostosEspeciales: 0, Subtotal: 0,
        PorcentajeIncrementoAnual: 0, SubtotalConIncremento: 0, PrecioUnitario: 0, Descuento: 0, Incremento: 0,
        PrecioFinalUnitario: 0, PrecioTotal: 0,
        ...(equipo.EsAscensor ? { CostoParadas: 0, CostoVelocidad: 0, CostoUso: 0 } : {})
    })),
    IndicadoresAscensores: { Utilidad: 0, PorcentajeUtilidad: 0 },
    IndicadoresEscaleras: { Utilidad: 0, PorcentajeUtilidad: 0 }
});

/** Formato de dinero de las tablas del cotizador. */
export const formatoMoneda = (valor?: number) => `$ ${(valor ?? 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

/** Acciones y estado de edición de las tablas de equipos (meta de la tabla). */
export interface ITablaEquiposMeta<T> {
    opciones: IOpcionesCotizacionServicios;
    filaEnEdicion: string | null;
    intentoGuardar: boolean;
    soloLectura: boolean;
    alCambiar: (id: string, campo: keyof T, valor: string | number) => void;
    alIniciarEdicion: (id: string) => void;
    alGuardar: (id: string) => void;
    alCancelar: () => void;
    alEliminar: (id: string) => void;
}
